import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../hooks/useAuth';
import { actingPartnerKeyFor } from './actingPartnerStorage';
import { logSessionIdentity } from 'helpers/sessionIdentityLog';
import { ownHouseTenantFor } from 'constants/houseTenant';

export const ActingPartnerContext = createContext(null);

/**
 * Which partner the admin is shopping and booking AS.
 *
 * ── Why this is session state and not page state ────────────────────────────
 * The choice has to outlive the screen it is made on. Start Chat and Shop Travel
 * are separate routes, and an admin routinely moves between them mid-task — a
 * conversation that produces an itinerary, then the booking flow. Held in the
 * page, the selection would reset on that navigation and the second screen would
 * quietly act for a different tenant than the first.
 *
 * It also has to outlive a reload, which is what sessionStorage is for here.
 *
 * ── Keyed by user, deliberately ─────────────────────────────────────────────
 * The storage key carries the signed-in user's id, so a second admin signing in
 * on the same machine starts with no selection instead of inheriting the
 * previous one. Booking for the wrong partner because the tab remembered
 * somebody else's choice is exactly the failure this whole feature exists to
 * prevent.
 *
 * sessionStorage rather than localStorage for the same reason the portal session
 * uses it: scoped to the tab, gone when it closes.
 *
 * ── What is stored ──────────────────────────────────────────────────────────
 *   channel          "b2b" | "b2c" — the first choice, and the partner filter
 *   partner          { partnerCode, name, branchCode, channel, currency }
 *   partnerUser      { id, name, email } — the PERSON bookings are filed under
 *   travellerUserId  the `user` row the embedded app books under
 *   isOwnAccount     true when the partner IS Zenvoya's own tenant
 *
 * `isOwnAccount` marks Zenvoya's OWN account: the frame books under the
 * session's own `travellerIdentity.agentUserId` (the operator is the person), and
 * the acting tenant is the house tenant for the chosen CHANNEL —
 * `ownHouseTenantFor` (constants/houseTenant): B2B → Zenvoya 1000001/1000011,
 * B2C → Zenvoya B2C 1000002/1000012. It is sent like any other acting tenant, so
 * the channel the operator picked is the channel the booking is made through.
 *
 * ── Three steps, and only the last one commits ──────────────────────────────
 * Channel → partner → person. Picking a partner is a DRAFT: it narrows the user
 * list and nothing more. The server call that resolves the booking identity
 * happens once, at the third step, when everything it needs is known — so there
 * is no window in which a partner is selected but the frame does not yet know
 * who it books as. `isReady` is false for the whole of that window, which is
 * what keeps the frame gated.
 *
 * Only a READY state is persisted. A half-made choice surviving a reload would
 * restore a partner with no identity behind it — visibly selected, unusable, and
 * indistinguishable from a bug.
 */

const load = (userId) => {
  try {
    const raw = window.sessionStorage.getItem(actingPartnerKeyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Both halves, not just the partner: a stored state without an identity is
    // the half-made choice this deliberately does not restore.
    if (!(parsed?.partner?.partnerCode && parsed?.travellerUserId)) return null;
    // An own-account choice is re-derived from its channel rather than trusted:
    // one saved before own-account followed the channel carries Zenvoya root
    // with no branch, whatever the channel says.
    if (parsed.isOwnAccount) {
      const house = ownHouseTenantFor(parsed.channel);
      return house ? { ...parsed, partner: { ...house, isOwn: true } } : null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const save = (userId, value) => {
  try {
    if (value) window.sessionStorage.setItem(actingPartnerKeyFor(userId), JSON.stringify(value));
    else window.sessionStorage.removeItem(actingPartnerKeyFor(userId));
  } catch {
    /* private mode — the in-memory copy still serves this tab */
  }
};

export function ActingPartnerProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id || user?.email || '';

  // Lazy initialiser: read storage once at mount rather than on every render.
  const [state, setState] = useState(() => (userId ? load(userId) : null));

  /**
   * Re-seed when the signed-in user changes, INCLUDING to nothing.
   *
   * Sign-out unmounts this provider today, but that is a fact about the shell's
   * routing rather than a guarantee — and a stale selection surviving a user
   * change is the one failure mode worth defending against directly.
   */
  useEffect(() => {
    setState(isAuthenticated && userId ? load(userId) : null);
  }, [isAuthenticated, userId]);

  /**
   * Persist only a state the frame could actually load. See the note above.
   */
  const commit = useCallback(
    (next) => {
      setState(next);
      save(userId, next?.partner?.partnerCode && next?.travellerUserId ? next : null);
      /**
       * Reprint the identity with the partner being acted FOR.
       *
       * The CRM session's own `partnerCode` is always Zenvoya's house tenant, so
       * the code logged at sign-in is not the one that scopes anything an
       * operator does on these screens — this selection is. Logging it here is
       * what makes `[session] admin` answer the same question its partnerAdmin
       * counterpart does: which tenant and branch is this about to book under.
       */
      logSessionIdentity('crm', 'acting as partner', {
        actingPartnerCode: next?.partner?.partnerCode ?? null,
        actingBranchCode: next?.partner?.branchCode ?? null,
        actingPartnerName: next?.partner?.name ?? null,
        // Null for Zenvoya's own account and for the agency fallback — see
        // `selectIdentity` below.
        actingPartnerUserEmail: next?.partnerUser?.email ?? null,
        isOwnAccount: !!next?.isOwnAccount,
      });
    },
    [userId],
  );

  /**
   * The channel filter. Changing it drops a real partner — it is now off-list.
   *
   * Zenvoya's OWN account is the exception: it exists on both channels, as a
   * different house tenant on each (B2B → 1000001, B2C → 1000002), so the
   * selection follows the channel instead of being cleared. Same operator, same
   * traveller identity — only the tenant it acts as changes, and the frame reloads
   * because the acting partner code is part of its key.
   */
  const setChannel = useCallback(
    (channel) => {
      setState((prev) => {
        if (prev?.channel === channel) return prev;
        const house = prev?.isOwnAccount ? ownHouseTenantFor(channel) : null;
        if (house && prev?.travellerUserId) {
          const next = { ...prev, channel, partner: { ...house, isOwn: true } };
          save(userId, next);
          // Same reprint `commit` does: the tenant this tab acts as just changed.
          logSessionIdentity('crm', 'acting as partner', {
            actingPartnerCode: house.partnerCode,
            actingBranchCode: house.branchCode,
            actingPartnerName: house.name,
            actingPartnerUserEmail: null,
            isOwnAccount: true,
          });
          return next;
        }
        const next = channel
          ? { channel, partner: null, partnerUser: null, travellerUserId: null, isOwnAccount: false }
          : null;
        save(userId, null);
        return next;
      });
    },
    [userId],
  );

  /**
   * Step two — a DRAFT. The partner narrows the user list; it does not yet say
   * who bookings are filed under, so no identity is set and `isReady` stays
   * false until step three lands.
   */
  const setPartner = useCallback(
    (partner) => {
      setState((prev) => ({
        channel: prev?.channel || null,
        partner: partner || null,
        partnerUser: null,
        travellerUserId: null,
        isOwnAccount: false,
      }));
      save(userId, null);
    },
    [userId],
  );

  /**
   * Step three — the commit. The caller has resolved the traveller identity, so
   * everything the frame needs arrives at once.
   *
   * `partnerUser` is null in exactly two cases, both deliberate: Zenvoya's own
   * account (the operator IS the person), and the agency fallback for a partner
   * with no active user to name.
   */
  const selectIdentity = useCallback(
    ({ channel, partner, partnerUser, travellerUserId, isOwnAccount }) => {
      commit({
        channel: channel || null,
        partner: partner || null,
        partnerUser: partnerUser || null,
        travellerUserId: travellerUserId || null,
        isOwnAccount: !!isOwnAccount,
      });
    },
    [commit],
  );

  const clear = useCallback(() => {
    setState(null);
    save(userId, null);
  }, [userId]);

  const value = useMemo(
    () => ({
      channel: state?.channel || null,
      partner: state?.partner || null,
      partnerUser: state?.partnerUser || null,
      travellerUserId: state?.travellerUserId || null,
      isOwnAccount: !!state?.isOwnAccount,
      /**
       * Every step done — the only state in which a frame may be shown.
       *
       * Keyed on `travellerUserId` rather than on `partnerUser`, because that is
       * the thing the frame actually needs and it is present in all three ending
       * shapes: a named partner user, Zenvoya's own operator, and the agency
       * fallback. Requiring `partnerUser` would gate the two legitimate cases
       * where there is nobody to name.
       */
      isReady: !!state?.channel && !!state?.partner && !!state?.travellerUserId,
      setChannel,
      setPartner,
      selectIdentity,
      clear,
    }),
    [state, setChannel, setPartner, selectIdentity, clear],
  );

  return <ActingPartnerContext.Provider value={value}>{children}</ActingPartnerContext.Provider>;
}
